import { RuleCombinator, RuleOperator } from 'utils/api/modules/memberGroups';
import { isMultiValueOperator, isNoValueOperator } from 'containers/MemberGroups/components/RuleBuilder/registry';

import { ProtectorType } from 'core/constants';

import {
  EntityValueType,
  FieldCondition,
  FieldConditionRule,
  RelevantFieldType,
} from 'utils/api/modules/entities/entities.types';

import { CHOICE_TYPES, COMPARABLE_SUBFIELD, EventFieldKind, IDENTITY_TYPES } from './kinds';

export { isMultiValueOperator, isNoValueOperator };

export const CONDITION_OPERATOR_LABELS: Record<RuleOperator, string> = {
  is: 'is',
  isNot: 'is not',
  isAnyOf: 'is any of',
  isNoneOf: 'is none of',
  contains: 'contains',
  doesNotContain: 'does not contain',
  startsWith: 'starts with',
  isEmpty: 'is empty',
  isNotEmpty: 'is not empty',
};

// Mirrors core-api's OPERATORS_BY_KIND exactly.
export const OPERATORS_BY_KIND: Record<EventFieldKind, RuleOperator[]> = {
  option: ['is', 'isNot', 'isAnyOf', 'isNoneOf', 'isEmpty', 'isNotEmpty'],
  text: ['is', 'isNot', 'contains', 'doesNotContain', 'startsWith', 'isEmpty', 'isNotEmpty'],
  date: ['isEmpty', 'isNotEmpty'],
};

export const operatorsForKind = (kind?: EventFieldKind): RuleOperator[] => OPERATORS_BY_KIND[kind ?? 'text'];

export const isValueOperator = (operator: RuleOperator): boolean => !isNoValueOperator(operator);

export interface TriggerAnswer {
  value?: EntityValueType;
  protectorType?: ProtectorType;
}

export type EntityValues = Record<string, TriggerAnswer | undefined>;

const subfieldKeys = (record: Record<string, unknown>, subfield: string): string[] => {
  const part = record[subfield];
  return part === undefined || part === null || part === '' ? [] : [String(part)];
};

export const answerKeys = (answer: unknown, protectorType?: ProtectorType): string[] => {
  if (answer === null || answer === undefined) {
    return [];
  }

  if (protectorType && CHOICE_TYPES.has(protectorType)) {
    return Array.isArray(answer)
      ? answer
          .filter(
            (item): item is { key: unknown } =>
              typeof item === 'object' && item !== null && (item as { key?: unknown }).key != null
          )
          .map((item) => String(item.key))
      : [];
  }

  if (protectorType && IDENTITY_TYPES.has(protectorType)) {
    const uuid = typeof answer === 'object' && answer !== null ? (answer as { uuid?: unknown }).uuid : undefined;
    return uuid === undefined || uuid === null ? [] : [String(uuid)];
  }

  const subfield = protectorType ? COMPARABLE_SUBFIELD[protectorType] : undefined;
  if (subfield) {
    return typeof answer === 'object' && answer !== null
      ? subfieldKeys(answer as Record<string, unknown>, subfield)
      : [];
  }

  if (typeof answer === 'string') {
    return answer.trim() === '' ? [] : [answer];
  }

  if (Array.isArray(answer) || typeof answer === 'object') {
    return [];
  }

  return [String(answer)];
};

const MILLISECONDS_ISO_FALLBACK = /^-?\d+$/;

/** Accepts both stored formats: epoch milliseconds (entities) and ISO strings (audits). */
export const isDateAnswered = (answer: unknown): boolean => {
  if (answer === null || answer === undefined || answer === '' || typeof answer === 'boolean') {
    return false;
  }

  if (typeof answer === 'number') {
    return Number.isFinite(answer);
  }

  if (typeof answer !== 'string') {
    return false;
  }

  if (MILLISECONDS_ISO_FALLBACK.test(answer.trim())) {
    return Number.isFinite(Number(answer));
  }

  return !Number.isNaN(Date.parse(answer));
};

export const MAX_CONDITIONS = 20;

export const isConditionComplete = (condition: FieldCondition): boolean => {
  if (!condition.attribute) {
    return false;
  }
  return isValueOperator(condition.operator) ? condition.values.length > 0 : true;
};

export const isConditionBlank = (condition: FieldCondition): boolean =>
  !condition.attribute && condition.values.length === 0;

export const incompleteConditionIndexes = (rule?: FieldConditionRule): number[] =>
  (rule?.conditions ?? []).reduce<number[]>(
    (found, condition, index) =>
      !isConditionBlank(condition) && !isConditionComplete(condition) ? [...found, index] : found,
    []
  );

export const completeConditions = (rule?: FieldConditionRule): FieldCondition[] =>
  (rule?.conditions ?? []).filter(isConditionComplete).slice(0, MAX_CONDITIONS);

const evaluateDate = (condition: FieldCondition, answer: unknown): boolean =>
  condition.operator === 'isEmpty' ? !isDateAnswered(answer) : isDateAnswered(answer);

const evaluateKeys = (condition: FieldCondition, keys: string[]): boolean => {
  const blank = keys.length === 0;
  const wanted = condition.values.map((value) => value.toLowerCase());
  const folded = keys.map((key) => key.toLowerCase());
  const text = folded.join(' ');
  const [first] = wanted;

  switch (condition.operator) {
    case 'isEmpty':
      return blank;
    case 'isNotEmpty':
      return !blank;
    case 'is':
    case 'isAnyOf':
      return folded.some((key) => wanted.includes(key));
    case 'isNot':
    case 'isNoneOf':
      return blank || !folded.some((key) => wanted.includes(key));
    case 'contains':
      return first !== undefined && text.includes(first);
    case 'doesNotContain':
      return blank || !(first !== undefined && text.includes(first));
    case 'startsWith':
      return !blank && first !== undefined && text.startsWith(first);
    default:
      return true;
  }
};

export const evaluateCondition = (condition: FieldCondition, values: EntityValues): boolean => {
  const answer = values[condition.attribute];
  const kind = answer?.protectorType;

  if (kind === ProtectorType.Date || kind === ProtectorType.DateTime || kind === ProtectorType.Time) {
    return evaluateDate(condition, answer?.value);
  }

  return evaluateKeys(condition, answerKeys(answer?.value, kind));
};

export const evaluateRule = (rule: FieldConditionRule | undefined, values: EntityValues): boolean => {
  const conditions = completeConditions(rule);
  if (conditions.length === 0) {
    return true;
  }
  return rule?.combinator === RuleCombinator.Any
    ? conditions.some((condition) => evaluateCondition(condition, values))
    : conditions.every((condition) => evaluateCondition(condition, values));
};

export const hasRule = (field: RelevantFieldType): boolean => completeConditions(field.conditions).length > 0;

const joinNames = (names: string[]): string => {
  if (names.length <= 1) {
    return names[0] ?? '';
  }
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
};

const describeCondition = (
  condition: FieldCondition,
  fieldName: (key: string) => string,
  valueName: (attribute: string, value: string) => string
): string => {
  const operator = CONDITION_OPERATOR_LABELS[condition.operator] ?? condition.operator;
  const name = fieldName(condition.attribute);
  if (!isValueOperator(condition.operator)) {
    return `${name} ${operator}`;
  }
  return `${name} ${operator} ${joinNames(condition.values.map((value) => valueName(condition.attribute, value)))}`;
};

const firesWhenUnanswered = (rule: FieldConditionRule | undefined): boolean => {
  const conditions = completeConditions(rule);
  if (conditions.length === 0) {
    return false;
  }
  const negative: RuleOperator[] = ['isNoneOf', 'isNot', 'doesNotContain'];
  if (!conditions.some((condition) => negative.includes(condition.operator))) {
    return false;
  }
  return evaluateRule(rule, {});
};

export const describeRule = (
  rule: FieldConditionRule | undefined,
  fieldName: (key: string) => string,
  valueName: (attribute: string, value: string) => string = (_, value) => value
): string => {
  const conditions = completeConditions(rule);
  if (conditions.length === 0) {
    return '';
  }
  const joiner = rule?.combinator === RuleCombinator.Any ? ' or ' : ' and ';
  const body = conditions.map((condition) => describeCondition(condition, fieldName, valueName)).join(joiner);
  if (!firesWhenUnanswered(rule)) {
    return `Only shown if ${body}`;
  }
  const triggers = new Set(conditions.map((condition) => condition.attribute));
  return triggers.size === 1
    ? `Only shown if ${body}, including before it is answered`
    : `Only shown if ${body}, including before those fields are answered`;
};

export const triggersOf = (field: RelevantFieldType): string[] => [
  ...new Set(completeConditions(field.conditions).map((condition) => condition.attribute)),
];

export const answerText = (values: EntityValues, key: string): string => {
  const answer = values[key]?.value;
  if (Array.isArray(answer)) {
    return answer.map((option) => (option as { value?: string }).value ?? String(option)).join(', ');
  }
  return String(answer ?? '');
};

export const explainCondition = (
  condition: FieldCondition,
  values: EntityValues,
  fieldName: (key: string) => string
): string => {
  const name = fieldName(condition.attribute);
  const answered = answerText(values, condition.attribute) !== '';
  if (condition.operator === 'isEmpty' || (!answered && condition.operator === 'isNoneOf')) {
    return `${name} has not been answered`;
  }
  if (condition.operator === 'isNotEmpty') {
    return `${name} has been answered`;
  }
  return `${name} is ${answerText(values, condition.attribute)}`;
};

export const explain = (field: RelevantFieldType, values: EntityValues, fieldName: (key: string) => string): string => {
  const conditions = completeConditions(field.conditions);
  const joiner = field.conditions?.combinator === RuleCombinator.Any ? ' or ' : ' and ';
  const body = conditions.map((condition) => explainCondition(condition, values, fieldName)).join(joiner);
  return body ? `Shown because ${body}.` : '';
};

export const anchorFor = (field: RelevantFieldType, order: string[]): string | undefined => {
  const present = triggersOf(field).filter((trigger) => order.includes(trigger));
  return present.length === 0
    ? undefined
    : present.reduce((latest, trigger) => (order.indexOf(trigger) > order.indexOf(latest) ? trigger : latest));
};

export const withAdjacency = (order: string[], shownFields: RelevantFieldType[]): string[] => {
  const result = [...order];
  order.forEach((key) => {
    const field = shownFields.find((candidate) => candidate.key === key);
    const anchor = field && hasRule(field) ? anchorFor(field, result) : undefined;
    if (!anchor) {
      return;
    }
    const from = result.indexOf(key);
    const to = result.indexOf(anchor);
    if (from === -1 || to === -1 || from === to + 1) {
      return;
    }
    result.splice(from, 1);
    result.splice(result.indexOf(anchor) + 1, 0, key);
  });
  return result;
};

export const newCondition = (): FieldCondition => ({ attribute: '', operator: 'isAnyOf', values: [] });

export const newRule = (): FieldConditionRule => ({
  combinator: RuleCombinator.All,
  conditions: [newCondition()],
});

export const committableRule = (rule: FieldConditionRule | undefined): FieldConditionRule | undefined => {
  const conditions = completeConditions(rule);
  return conditions.length === 0 || !rule ? undefined : { ...rule, conditions };
};
